import { z } from 'zod';
// A forma de concessão é do catálogo — reexportar aqui criaria uma segunda
// definição da mesma união (o front do totvs já importa do sales pelo mesmo
// motivo).
import type {
  GrantPolicyParams,
  PerkGrantPolicyKind,
} from '@/modules/catalog/domain/entities/catalog.entity';

export const ActivePlatformSchema = z.object({
  scopeId: z.string(),
  /** Slug técnico do scope. Rótulo de tela é o `platformName`. */
  platform: z.string(),
  platformName: z.string(),
  /** BUs atendidas pela plataforma; vazio nos painéis internos. */
  tenants: z.array(z.string()),
  isActive: z.boolean(),
});

export const UserSchema = z.object({
  accountId: z.string(),
  userId: z.string(),
  name: z.string(),
  email: z.string().email(),
  createdAt: z.string(),
  activePlatforms: z.array(ActivePlatformSchema),
});

export const UsersListResponseSchema = z.object({
  items: z.array(UserSchema),
  total: z.number(),
  page: z.number(),
  perPage: z.number(),
});

export type ActivePlatform = z.infer<typeof ActivePlatformSchema>;
export type User = z.infer<typeof UserSchema>;
export type UsersListResponse = z.infer<typeof UsersListResponseSchema>;

export interface ListUsersInput {
  page?: number;
  perPage?: number;
  email?: string;
}

/** Provisiona uma conta + usuário (sem senha; o usuário define depois por e-mail). */
export interface ProvisionUserInput {
  email: string;
  name: string;
  /**
   * Opcional: dá acesso a esta plataforma no mesmo ato. Preenchido, o convite de
   * primeiro acesso passa a sair da criação do perfil — um e-mail só, em vez de
   * dois (o segundo invalidaria o link do primeiro).
   */
  scopeId?: string;
}

/**
 * Resultado do cadastro: `created` = conta nova (`false` = e-mail já cadastrado,
 * no-op idempotente); `accessGranted` = o perfil de plataforma foi criado no
 * mesmo ato, e é dele que sai o convite.
 */
export interface ProvisionResult {
  created: boolean;
  accessGranted: boolean;
}

/** Reenvio do convite de primeiro acesso: `sent: false` = pessoa já definiu a senha. */
export interface ResendInviteResult {
  sent: boolean;
}

/** Cria um PlatformProfile (dá acesso a um novo scope). */
/**
 * O iamEditor trazendo alguém para uma plataforma que administra
 * (`POST /admin/identity/scoped-users`): conta, se não existe, e acesso, numa
 * chamada. Sem `name`, o back exige a conta já existente — é o modo do detalhe,
 * onde a pessoa já está ali.
 */
export interface ProvisionScopedUserInput {
  email: string;
  scopeId: string;
  name?: string;
}

/** `profileCreated: false` = a pessoa já tinha acesso ativo; nada mudou. */
export interface ProvisionScopedUserResult {
  accountCreated: boolean;
  profileCreated: boolean;
  userId: string;
  platformProfileId: string;
}

export interface CreatePlatformProfileInput {
  userId: string;
  name: string;
  scopeId: string;
}

export type GrantedPerkStatus =
  | 'active'
  | 'expired'
  | 'revoked'
  | 'depleted'
  | 'suspended';

/**
 * Snapshot dos parâmetros da grant policy no momento da concessão (espelha
 * `GrantPolicyParamsSnapshot` no back). Datas chegam como ISO string via HTTP.
 */
export interface GrantPolicyParamsSnapshot {
  remainingUsages?: number;
  maxUsages?: number;
  expiresAt?: string;
  renewAt?: string;
  renewEveryInMilliseconds?: number;
}

/**
 * De onde a concessão veio. Substituiu o par `productId`/`contractId`
 * obrigatório: agora a origem é polimórfica e o grant carrega uma lista
 * append-only delas — uma compra, uma concessão manual e um bootstrap do
 * sistema podem coexistir no mesmo pacote.
 */
export type GrantOrigin = {
  dedupKey: string;
  /**
   * O que **esta** origem contribuiu, em termos relativos (duração, saldo) — não
   * o estado que saiu daquilo, que é o `grantPolicyParamsSnapshot` do pacote. É
   * o que permite montar a linha do tempo de acumulação.
   */
  applied: GrantPolicyParams;
  /** Lacuna coberta por esta origem — o pacote já tinha vencido quando ela chegou. */
  gapBeforeInMilliseconds: number;
  state: 'live' | 'suspended' | 'reverted';
  appliedAt: string;
  /** Quando saiu de `live`; `null` enquanto nunca mudou de estado. */
  stateChangedAt: string | null;
} & (
  | { kind: 'PURCHASE'; productId: string; contractId: string }
  | { kind: 'MANUAL'; grantedByUserId: string; presetId: string | null }
  | { kind: 'SYSTEM'; reason: string }
);

/** Perk concedido a um platformProfile (GET /admin/perks/profile/:id). */
export interface GrantedPerk {
  id: string;
  platformProfileId: string;
  perkId: string;
  perkKey: string;
  perkType: string;
  /** A forma da concessão: janela mostra prazo, crédito mostra saldo. */
  grantPolicyKind: PerkGrantPolicyKind;
  origins: GrantOrigin[];
  status: GrantedPerkStatus;
  statusReason: string | null;
  grantPolicyParamsSnapshot: GrantPolicyParamsSnapshot;
}

/**
 * Um perk **efetivo** do perfil (`GET /admin/perks/profile/:id/effective`): o
 * que autoriza de fato, depois do fecho de `grantsPerkKeys` e do portão de
 * `requiresPerkKeys`. Difere do `GrantedPerk` em duas pontas: cabe aqui o perk
 * **derivado** (sem pacote por trás), e some daqui o pacote inerte por portão.
 */
export interface EffectivePerk {
  /** `null` quando derivado — não há pacote a apontar. */
  grantedPerkId: string | null;
  perkId: string;
  perkKey: string;
  perkType: string;
  grantPolicyKind: PerkGrantPolicyKind;
  /** Derivado é sempre `null`: vive enquanto um pai viver. */
  expiresAt: string | null;
  derived: boolean;
  /** Pais retidos que derivam este perk — todos eles. Vazio se `derived: false`. */
  derivedFrom: { perkId: string; perkKey: string }[];
}

/**
 * Uma concessão do lote. `presetId` é só trilha: registra de qual preset o
 * operador puxou a linha, não cria vínculo — os valores já vêm clonados.
 */
export interface PerkGrantBatchAddItem {
  perkId: string;
  grantPolicyParams?: GrantPolicyParams;
  presetId?: string;
}

/**
 * Revoga **um pacote** (`grantedPerkId`), não o par `(perfil, perk)`. `reason` é
 * obrigatório: no lote não existe motivo genérico de fallback.
 */
export interface PerkGrantBatchRevokeItem {
  grantedPerkId: string;
  reason: string;
}

/**
 * O diff inteiro do perfil (`POST /admin/perks/batch`). Diferente dos alvos de
 * catálogo, `perkId` repetido em `grant` é legítimo — soma na janela ou abre um
 * pacote paralelo de crédito.
 */
export interface ApplyPerkGrantBatchInput {
  platformProfileId: string;
  grant: PerkGrantBatchAddItem[];
  revoke: PerkGrantBatchRevokeItem[];
}

export interface PerkGrantBatchResult {
  granted: GrantedPerk[];
  revoked: PerkGrantBatchRevokeItem[];
}

export type Gender = 'MALE' | 'FEMALE' | 'OTHER';

export interface UpdateUserInput {
  name?: string;
  /** `null` limpa o campo; `undefined` deixa como está. */
  gender?: Gender | null;
  doc?: string | null;
  phone?: string | null;
  address?: {
    street: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
    number?: string | null;
    neighborhood?: string | null;
    complement?: string | null;
  };
}

/**
 * Método de login vinculado a uma conta (`authMethods` no detalhe).
 *
 * **Sem `email`**: o back parou de espelhar o e-mail do provedor (C11). Ele era
 * redundante — a regra é que um método federado só vincula se o e-mail dele for
 * igual ao da conta, então era sempre o e-mail da própria conta.
 */
export const AuthMethodSchema = z.object({
  // `string`, não `z.enum`: um provedor que o back passe a aceitar antes deste
  // painel conhecê-lo derrubaria o parse do detalhe de usuário inteiro. O label
  // cai no próprio valor (`AUTH_METHOD_LABEL[m] ?? m`).
  method: z.string(),
  linkedAt: z.string(),
});

export type AuthMethod = z.infer<typeof AuthMethodSchema>;
export type AuthMethodKind = AuthMethod['method'];

export const UserPlatformDetailSchema = z.object({
  id: z.string(),
  scopeId: z.string(),
  /** Slug técnico do scope. Rótulo de tela é o `platformName`. */
  platform: z.string(),
  platformName: z.string(),
  /** BUs atendidas pela plataforma; vazio nos painéis internos. */
  tenants: z.array(z.string()),
  profileName: z.string().nullable().optional(),
  isActive: z.boolean(),
  /** Endereço web da plataforma; sem ele, não há para onde levar a máscara. */
  url: z.string().nullable().optional(),
  lastAccessAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const AddressSchema = z.object({
  street: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  zipCode: z.string().nullable().optional(),
  number: z.string().nullable().optional(),
  neighborhood: z.string().nullable().optional(),
  complement: z.string().nullable().optional(),
});

export const UserDetailSchema = z.object({
  userId: z.string(),
  accountId: z.string(),
  email: z.string(),
  isVerified: z.boolean(),
  verifiedAt: z.string().nullable().optional(),
  authMethods: z.array(AuthMethodSchema).optional(),
  name: z.string(),
  gender: z.enum(['MALE', 'FEMALE', 'OTHER']).nullable().optional(),
  doc: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  address: AddressSchema.nullable().optional(),
  accountCreatedAt: z.string(),
  userCreatedAt: z.string(),
  userUpdatedAt: z.string(),
  platforms: z.array(UserPlatformDetailSchema),
});

export type UserPlatformDetail = z.infer<typeof UserPlatformDetailSchema>;
export type Address = z.infer<typeof AddressSchema>;
export type UserDetail = z.infer<typeof UserDetailSchema>;

// --- Eventos (audit log) de Account/User ---

/** Entidades auditáveis do Identity (espelha `IdentityEntityType` do monolith). */
export type IdentityEntityType =
  | 'Account'
  | 'User'
  | 'AuthProviderIntent'
  | 'OperationalAlert'
  | 'PlatformProfile';

export const IdentityEventSchema = z.object({
  id: z.string(),
  eventType: z.string(),
  actorPlatformProfileId: z.string().nullable(),
  /**
   * Ator resolvido pelo back. `.optional()` para que front novo continue lendo
   * back antigo — o formato é o que o `actorName` do sales já usava.
   */
  actorName: z.string().nullable().optional(),
  /** Pessoa por trás do perfil do ator, para linkar `/management/users/:userId`. */
  actorUserId: z.string().nullable().optional(),
  /**
   * Humano real por trás de uma ação feita sob act-as. Fora de máscara vem
   * `null` — e `actorName` acima continua sendo o perfil **mascarado**.
   */
  realActorName: z.string().nullable().optional(),
  realActorUserId: z.string().nullable().optional(),
  reason: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  correlationId: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type IdentityEvent = z.infer<typeof IdentityEventSchema>;

export const IdentityEventsResponseSchema = z.object({
  items: z.array(IdentityEventSchema),
  total: z.number(),
  page: z.number(),
  perPage: z.number(),
});
export type IdentityEventsResponse = z.infer<
  typeof IdentityEventsResponseSchema
>;

/** Evento do Identity listado por `correlationId` (row do webhook inbox do Sales). */
export const IdentityCorrelatedEntityEventSchema = IdentityEventSchema.extend({
  entityType: z.string(),
  entityId: z.string(),
});
export type IdentityCorrelatedEntityEvent = z.infer<
  typeof IdentityCorrelatedEntityEventSchema
>;

export const IdentityCorrelatedEntityEventsResponseSchema =
  IdentityEventsResponseSchema.extend({
    items: z.array(IdentityCorrelatedEntityEventSchema),
  });
export type IdentityCorrelatedEntityEventsResponse = z.infer<
  typeof IdentityCorrelatedEntityEventsResponseSchema
>;

// --- Alertas operacionais do Identity (Central de Pendências) ---

/**
 * Status do alerta — maiúsculo, `OperationalAlertStatus` do shared do monolith,
 * **não** o vocabulário minúsculo do Sales. O feed agregado da Central converte
 * (ver `identityPendingSource`).
 */
export type IdentityOperationalAlertStatus = 'OPEN' | 'RESOLVED' | 'DISMISSED';

/**
 * Formato HTTP de um `OperationalAlert` do Identity — espelha
 * `IdentityOperationalAlertOutputDto` do monolith. `type`/`entityType` chegam
 * como string livre (o genérico do shared não conhece o enum do módulo).
 */
export interface IdentityOperationalAlert {
  id: string;
  type: string;
  entityType: string;
  entityId: string;
  status: IdentityOperationalAlertStatus;
  inThisStatusSince: string;
  details: Record<string, unknown>;
  /**
   * Só em `duplicate_document`: o documento que o grupo ainda compartilha,
   * oculto (`***.456.789-**`); `null` quando o grupo já não divide documento.
   */
  hiddenDocument?: string | null;
  resolutionNote: string | null;
  resolvedByPlatformProfileId: string | null;
  resolutionAttachmentFileId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * `GET /admin/identity/users/:userId/duplicate-document` — os outros `User` com
 * o mesmo CPF/CNPJ. Rota só de admin (`IDENTITY_ALERT_ROLES`), não do
 * `iamEditor` que também abre a página de detalhe.
 */
export interface UserDuplicateDocument {
  otherUserIds: string[];
}

export interface ListIdentityOperationalAlertsInput {
  page?: number;
  perPage?: number;
  status?: IdentityOperationalAlertStatus;
  type?: string;
  entityType?: string;
  entityId?: string;
  /** `asc` = a mais antiga primeiro (a ordem da Central de Pendências). Default do back: `desc`. */
  order?: 'asc' | 'desc';
}

/** Uma opção da faceta de tipo: o `type` cru do back e quantos alertas há dele. */
export interface OperationalAlertFacet {
  value: string;
  count: number;
}

/** Corpo de `resolve`/`dismiss` — espelha `CloseIdentityOperationalAlertDto`. */
export interface CloseIdentityOperationalAlertInput {
  note: string;
  attachmentFileId?: string;
}

export interface IdentityOperationalAlertsResponse {
  items: IdentityOperationalAlert[];
  total: number;
  page: number;
  perPage: number;
}

/**
 * `entityType` do histórico de auditoria do alerta
 * (`GET /admin/identity/entities/:entityType/:id/events`) — espelha
 * `IdentityEntityType.OPERATIONAL_ALERT` do monolith.
 */
export const IDENTITY_OPERATIONAL_ALERT_ENTITY_TYPE = 'OperationalAlert';

// --- Eventos (audit log) de concessão/revogação de perk ---

/**
 * Mesma forma do `IdentityEvent` mais a `perkKey` — o audit log do Delivery é
 * escrito por `GrantedPerk`, e é o back que traduz o id para a key legível
 * (`GET /admin/perks/profile/:id/events`). É por estes eventos que se responde
 * "quem deu essa role"; a rota do Identity só enxerga `identity_entity_events`.
 */
export const PerkEventSchema = IdentityEventSchema.extend({
  perkKey: z.string().nullable(),
});
export type PerkEvent = z.infer<typeof PerkEventSchema>;

export const PerkEventsResponseSchema = z.object({
  items: z.array(PerkEventSchema),
  total: z.number(),
  page: z.number(),
  perPage: z.number(),
});
export type PerkEventsResponse = z.infer<typeof PerkEventsResponseSchema>;
